import { connect } from "react-redux";
import moment from "moment-timezone";
import React, { Component } from "react";

import CardSecondary from "@/components/UI/Card/CardSecondary";
import { daySelectHandler } from "@/lib/helpers/daySelectHandler";
import {
  DesktopCareTeamLoader,
  MobileCareTeamLoader,
} from "@/components/Common/Loaders/CareTeamLoader";
import { getPatientProfile } from "@/redux/actions/auth";
import { getQueryParams } from "@/utils/filters";
import { getScheduleForToday, isCareTeamRestricted } from "@/utils/scheduleUtils";
import { parseErrorMessage } from "@/lib/helpers/parseErrorMessage";
import PatientCareTeamSchedule from "@/components/Patient/Schedule/PatientCareTeam";
import { PaywallReminder } from "@/features/dashboard/components/reminder-banner/PaywallReminder";
import { ReminderContainer } from "@/features/dashboard/components/reminder-banner/ReminderContainer";
import { scheduleRequests } from "@/api/(requests)/schedule";
import { ScheduleSuccess } from "@/components/Patient/Schedule";
import { ScheduleWidgets } from "@/portals/(shared)/(schedule)/ScheduleWidgets";
import { sharedRequests } from "@/api/(requests)/shared";
import { showAlertWithAction } from "@/redux/actions/alerts";
/**
 * This component is used in two different ways:
 *  1. Creating new schedule with `mode: 'new'`
 *  2. Rescheduling/editing with `mode: 'edit'`
 *
 *  Note: `mode` is passed as `location.state.mode` property via in react-router.
 *        Reschedule will require a queryParam `provider-id` to reschedule with same provider
 */

// Ninety day care status
const NOT_ASSIGNED = "not_assigned";

class Schedule extends Component {
  constructor(props) {
    super(props);
    let mode = getQueryParams("mode", this.props.location.search);

    if (!mode) {
      props.history.push("/app");
    } else {
      /** TODO: revert back provider-id to queryParams */
      let providerId = getQueryParams("provider_id", this.props.location.search) || null;
      let call_id = getQueryParams("call_id", this.props.location.search) || null;

      this.state = {
        appointmentDetail: null,
        call_id,
        dateStr: "",
        earliestDateAllowed: null,
        isDisabled: true,
        mode,
        patientDetails: null,
        providerId,
        selectedDate: null,
        selectedTimeSlot: null,
        slotsCount: 0,
        timeSlots: null,
        timeSlotsLoading: true,
      };
    }
  }

  getAvailableSchedules = (dateString, providerId) => {
    let urlParams = {
      call_id: getQueryParams("call_id", this.props.location.search),
      dateString,
      providerId,
      tz: this.props.timezone,
    };

    return sharedRequests.available_schedules({ urlParams });
  };

  scheduleCall = (data) => {
    let { date, time } = data;
    const { timezone } = this.props;

    switch (this.state.mode) {
      case "new": {
        return scheduleRequests.post_schedule({
          data: {
            ...data,
            timestamp: `${date} ${time}`,
            timezone,
          },
        });
      }

      case "edit": {
        return scheduleRequests.put_schedule({
          data: {
            ...data,
            call_id: this.state.call_id,
            timestamp: `${date} ${time}`,
            timezone,
          },
        });
      }

      default:
        return;
    }
  };

  onTimeSlotClick = (slotValue) => {
    this.setState({
      isDisabled: false,
      selectedTimeSlot: slotValue,
    });
  };

  onConfirmSlotSubmit = () => {
    let localTZ = this.props.timezone;
    let combinedDateTime = this.state.selectedDate + " " + this.state.selectedTimeSlot?.time_slot;
    let UTCDate = moment
      .tz(combinedDateTime, "YYYY-MM-DD hh:mm a", localTZ)
      .utc()
      .format("YYYY-MM-DD");
    let UTCTime = moment.tz(combinedDateTime, "YYYY-MM-DD hh:mm a", localTZ).utc().format("HH:mm");
    let local_date_time = moment
      .tz(combinedDateTime, "YYYY-MM-DD hh:mm a", localTZ)
      .format("YYYY-MM-DD HH:mm");
    let data = {
      date: UTCDate,
      local_date_time,
      provider_id: this.state.selectedTimeSlot?.provider_id,
      time: UTCTime,
    };

    if (this.state.providerType) {
      if (this.state.providerType === "Prescriber") {
        data.provider_type = "PRESCRIBE";
      } else {
        data.provider_type = "BEHAVIORAL";
      }
    } else {
      data.provider_type = "BEHAVIORAL_INTAKE";
    }

    this.scheduleCall(data)
      .then((cleanResponse) => {
        let appointmentDetail = cleanResponse.appointment_details;

        this.setState({
          appointmentDetail: appointmentDetail || "rescheduled",
        });

        this.props.getPatientProfile();
      })
      .catch((err) => {
        this.props.showAlertWithAction(parseErrorMessage(err));
        console.log(err);
      });
  };

  relevantProviderTypes = ["Behavioral Specialist", "Prescriber"];

  fetchCall = async () => {
    const scheduleCallList = await getScheduleForToday();

    return scheduleCallList.schedule.find((call) => call.callId === this.state.call_id);
  };

  async getEarliestDateAllowedToSchedule(providerType) {
    const searchParams = {
      providerType: providerType,
      tz: this.props.timezone,
    };
    const earliestDateAllowedResponse = await sharedRequests.get_earliest_date_allowed_to_schedule({
      searchParams,
    });

    return earliestDateAllowedResponse?.earliest_date_allowed;
  }

  async componentDidMount() {
    const call = this.state.call_id ? await this.fetchCall() : null;
    const careTeamMembers = this.props.patientProfile.care_team_members.filter((member) =>
      this.relevantProviderTypes.includes(member.type),
    );
    let defaultProviderId = getQueryParams("provider_id", this.props.location.search) || undefined;
    let defaultProviderType;

    const availableCareTeamMembers = careTeamMembers.map((member) => {
      const newMember = { ...member };

      newMember["disabled"] = isCareTeamRestricted(member["type"], this.props.patientProfile);
      if (!defaultProviderId && !newMember["disabled"]) {
        defaultProviderId = newMember?.provider_id;
        defaultProviderType = newMember?.type;
      } else if (
        defaultProviderId === newMember?.provider_id &&
        defaultProviderType === undefined
      ) {
        defaultProviderType = newMember?.type;
      }

      return newMember;
    });

    const earliestDateAllowed = await this.getEarliestDateAllowedToSchedule(defaultProviderType);

    this.setState(
      {
        call,
        careTeamMembers: availableCareTeamMembers,
        earliestDateAllowed,
        patientDetails: this.props.patientProfile,
        providerId: defaultProviderId,
        providerType: defaultProviderType,
      },
      () => this.resetDaySelection(),
    );
  }

  careTeamMemberSelect = (providerId, providerType) => {
    let newState = { providerId: providerId };

    if (providerType) {
      newState["providerType"] = providerType;
    }
    this.setState(newState);
  };

  async componentDidUpdate(prevProps, prevState) {
    if (prevState.providerId && prevState.providerId !== this.state.providerId) {
      await this.resetDaySelection();
    }
  }

  async resetDaySelection() {
    const searchDate = this.state.selectedDate || this.state.earliestDateAllowed;

    if (searchDate) {
      daySelectHandler(searchDate, this);
    }
  }

  renderCareTeamSection = () => {
    return (
      <div className="Schedule__profile-info col-12 col-lg-10 p-lg-4 pb-lg-5">
        <CardSecondary className="py-4 px-3 px-lg-4">
          <div style={{ minHeight: "110px" }}>
            <p className="fs-18 pb-1">
              <strong>Care Team</strong>
            </p>
            <hr />
            {this.state.careTeamMembers && this.state.careTeamMembers.length > 0 ? (
              <PatientCareTeamSchedule
                careTeamMembers={this.state.careTeamMembers}
                onMemberSelect={this.careTeamMemberSelect}
                patientDetails={this.state.patientDetails}
                providerId={this.state.providerId}
              />
            ) : (
              <div>
                <DesktopCareTeamLoader classes="d-lg-block d-none" />
                <MobileCareTeamLoader classes="d-block d-lg-none" />
              </div>
            )}
          </div>
        </CardSecondary>
      </div>
    );
  };

  render() {
    const { patientCardInfo, patientProfile } = this.props;
    const { showPaywall } = patientCardInfo ?? {};

    if (patientProfile.status === "INACTIVE") {
      this.props.history.push("/app/schedule/inactive");
    }

    let timeSlotConf = {
      isDisabled: this.state.isDisabled,
      selectedDate: this.state.selectedDate,
      selectedTimeSlot: this.state.selectedTimeSlot,
    };

    let showCareTeamSection = false;

    /** checking if to show care team section */
    if (patientProfile && patientProfile.ninety_day_care !== NOT_ASSIGNED) {
      showCareTeamSection = true;
    }

    return (
      <div data-public="true">
        <ReminderContainer>{showPaywall && <PaywallReminder />}</ReminderContainer>

        <div className="Schedule__wpr p-lg-4 mx-auto row no-gutters">
          {this.state.appointmentDetail && (
            <ScheduleSuccess behavioralSpecialist={this.state.appointmentDetail.provider_name} />
          )}

          {showCareTeamSection && this.renderCareTeamSection()}
          <ScheduleWidgets
            dateStr={this.state.dateStr}
            onConfirmSlotSubmit={this.onConfirmSlotSubmit}
            onTimeSlotClick={this.onTimeSlotClick}
            selectedDate={this.state.selectedDate && moment(this.state.selectedDate)}
            that={this}
            timeSlotConf={timeSlotConf}
            timeSlots={this.state.timeSlots}
            timeSlotsLoading={this.state.timeSlotsLoading}
            timezone={this.props.timezone}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    flags: state.flags,
    patientCardInfo: state.patientCardInfo,
    patientProfile: state.auth.profile,
    timezone: state.auth.timezone,
  };
};

export default connect(mapStateToProps, { getPatientProfile, showAlertWithAction })(Schedule);
